import _ from 'lodash';

export const formatName = (profile: Record<string, any> | null | undefined): string => {
  const firstName = _.get(profile, 'firstName');
  const lastName = _.get(profile, 'lastName');
  const fullName = _.get(profile, 'fullName');
  const maxLength = 100;

  if (typeof fullName === 'string') {
    return _.truncate(fullName, { length: maxLength * 2, omission: '' });
  }

  const truncatedFirstName = _.truncate(firstName, { length: maxLength, omission: '' });
  const truncatedLastName = _.truncate(lastName, { length: maxLength, omission: '' });
  return !(_.isNil(firstName) && _.isNil(lastName)) ? `${truncatedFirstName} ${truncatedLastName}`.trim() : 'unknown';
};

export const formatNameWithLastInitial = (profile: Record<string, any> | undefined): string => {
  const formattedName = formatName(profile);
  const [firstName, ...lastNameParts] = _.split(formattedName, ' ');
  const lastName = lastNameParts.join(' ');

  // fullName could possibly be one name with no spaces. formatName can also just return 'unknown';
  if (!lastName) {
    return firstName;
  }

  return `${firstName} ${lastName[0]}.`;
};

export const isConsumerUser = (user: Record<string, any> | null): boolean =>
  Boolean(user?.profile?.type === 'user' || _.isNil(user?.profile?.type));

export const isSuperAdmin = (user: Record<string, any> | null): boolean =>
  Boolean(user?.profile?.type === 'superAdmin');

export const isDispensaryUser = (user: Record<string, any> | null): boolean =>
  Boolean(user?.profile?.type === 'dispensaryUser');

export const isInternalEmail = (email: string): boolean =>
  _.includes(email, '@dutchie.com') || _.includes(email, '@leaflogix.com') || _.includes(email, '@greenbits.com');

// this function has a somewhat misleading name
// it does not check if the user has admin over a chain, but rather if they are an admin in any chain
export const isChainAdmin = (user: Record<string, any> | null): boolean =>
  Boolean(
    user?.profile?.chainID &&
      isDispensaryUser(user) &&
      (user.profile.permissions?.allLocations || user.profile.permissions.dispensaryIds?.length > 1)
  );

export const isAdminAccessible = (user: Record<string, any> | null): boolean =>
  isSuperAdmin(user) || isDispensaryUser(user);

export const superAdminCan = (permissions: Record<string, any> | null, permission: string): boolean => {
  let hasPermission = true;

  if (isRecord(permissions) && (permissions[permission] === false || !(permission in permissions))) {
    hasPermission = false;
  }

  if (permission === 'menuReview' && !permissions?.menuReview) {
    hasPermission = false;
  }

  return hasPermission;
};

function isRecord<K extends number | string | symbol, T>(
  record: Record<K, T> | null | undefined
): record is Record<K, T> {
  return !_.isEmpty(record);
}
